import { useCallback, useRef, useState } from 'react';
import {
  CARGO_PROBLEM_MESSAGE_ATTACHMENTS_MAX,
  type CargoProblemMessageAttachmentInput,
} from '@magimp/shared';
import {
  cargoProblemExpiredObjectKey,
  isCargoProblemVideoMime,
  uploadCargoProblemAttachment,
  validateCargoProblemAttachment,
  type CargoProblemAttachmentError,
} from './useCargoProblemAttachments.js';

export type CargoProblemMessageAttachmentStatus = 'uploading' | 'ready' | 'error';

export interface CargoProblemMessageAttachment {
  id: string;
  file: File;
  status: CargoProblemMessageAttachmentStatus;
  uploaded?: CargoProblemMessageAttachmentInput | undefined;
}

/**
 * @aiPurpose composer-side attachment state. Bytes travel the moment a file is
 *   picked, so pressing send only commits already-stored objects.
 * @aiInvariant sending is refused unless EVERY selected attachment is stored —
 *   a still-uploading or failed one would commit a message missing its own
 *   evidence, and a failed upload stays unresolved until it is retried or
 *   removed.
 * @aiInvariant one expired video key is recovered exactly once per send: the
 *   expired object is re-uploaded and the SAME body is committed again, so a
 *   long upload that outlived its key does not cost the typed message.
 */
export function useCargoProblemMessageAttachments(problemId: string) {
  const nextId = useRef(0);
  const itemsRef = useRef<CargoProblemMessageAttachment[]>([]);
  const [items, setItems] = useState<CargoProblemMessageAttachment[]>([]);

  const publish = useCallback((next: CargoProblemMessageAttachment[]) => {
    itemsRef.current = next;
    setItems(next);
  }, []);

  const patch = useCallback(
    (id: string, change: Partial<CargoProblemMessageAttachment>) => {
      publish(itemsRef.current.map((item) => (item.id === id ? { ...item, ...change } : item)));
    },
    [publish],
  );

  const upload = useCallback(
    async (id: string, file: File) => {
      try {
        const uploaded = await uploadCargoProblemAttachment(problemId, file, 'workflow');
        patch(id, { status: 'ready', uploaded });
      } catch {
        patch(id, { status: 'error', uploaded: undefined });
      }
    },
    [patch, problemId],
  );

  const add = useCallback(
    (files: readonly File[]): CargoProblemAttachmentError | null => {
      if (itemsRef.current.length + files.length > CARGO_PROBLEM_MESSAGE_ATTACHMENTS_MAX) {
        return 'count';
      }
      for (const file of files) {
        const error = validateCargoProblemAttachment(file);
        if (error) return error;
      }
      const additions = files.map((file) => ({
        id: `${file.name}-${file.lastModified}-${nextId.current++}`,
        file,
        status: 'uploading' as const,
      }));
      publish([...itemsRef.current, ...additions]);
      for (const item of additions) void upload(item.id, item.file);
      return null;
    },
    [publish, upload],
  );

  const remove = useCallback(
    (id: string) => {
      publish(itemsRef.current.filter((item) => item.id !== id));
    },
    [publish],
  );

  const retry = useCallback(
    (id: string) => {
      const item = itemsRef.current.find((candidate) => candidate.id === id);
      if (!item || item.status === 'uploading') return;
      patch(id, { status: 'uploading', uploaded: undefined });
      void upload(id, item.file);
    },
    [patch, upload],
  );

  const clear = useCallback(() => publish([]), [publish]);

  const sendWith = useCallback(
    async <T>(commit: (attachments: CargoProblemMessageAttachmentInput[]) => Promise<T>) => {
      if (itemsRef.current.some((item) => item.status !== 'ready')) {
        throw new Error('cargo-problem message attachments are not ready');
      }
      const ready = () =>
        itemsRef.current.flatMap((item) => (item.uploaded ? [item.uploaded] : []));
      try {
        return await commit(ready());
      } catch (error) {
        const expiredKey = cargoProblemExpiredObjectKey(error);
        const expired = itemsRef.current.find((item) => item.uploaded?.objectKey === expiredKey);
        if (!expiredKey || !expired || !isCargoProblemVideoMime(expired.file.type)) throw error;
        patch(expired.id, { status: 'uploading', uploaded: undefined });
        await upload(expired.id, expired.file);
        if (itemsRef.current.some((item) => item.status !== 'ready')) throw error;
        return await commit(ready());
      }
    },
    [patch, upload],
  );

  const ready = items.every((item) => item.status === 'ready');

  return { add, clear, items, ready, remove, retry, sendWith };
}
